import {
  IServicepointData,
  IServicepointDetail,
  IServicepointDetailData,
  IServicepointLimitsData,
  IServicepointParams,
  IUpdateServicepoint,
  ServicepointServiceSyncResponse,
  SolCustomerOrderType,
} from "@/entities";
import { BACKEND_BASE_URL, IGenericResponse, IPageResponse, IResponseError } from "@/services";
import {
  IServicePointReportRequest,
  IServicepointService,
} from "@/services/backend/servicepoint/servicepoint.types";
import { handleResponse } from "@/services/handlers";
import Vue from "vue";
import { i18n } from "@/i18n";

export class ServicepointService implements IServicepointService {
  async getServicepoints(
    params: IServicepointParams
  ): Promise<IPageResponse<IServicepointData> | undefined> {
    const { response, error } = await handleResponse<
      IPageResponse<IServicepointData>,
      IResponseError
    >(Vue.$axios.get(`${BACKEND_BASE_URL}/api/configuration/v1/service-point`, { params }));
    if (error) {
      return undefined;
    } else {
      return response?.data;
    }
  }

  async getServicepoint(idServicepoint: number): Promise<IServicepointDetailData | undefined> {
    const { response, error } = await handleResponse<IServicepointDetailData, IResponseError>(
      Vue.$axios.get(`${BACKEND_BASE_URL}/api/configuration/v1/service-point/${idServicepoint}`)
    );
    if (error) {
      return undefined;
    } else {
      return response?.data;
    }
  }

  async getServicepointLimits(
    servicepointId: number,
    orderType: SolCustomerOrderType
  ): Promise<IServicepointLimitsData | undefined> {
    const params = new URLSearchParams();
    params.append("servicepointId", String(servicepointId));
    params.append("orderType", orderType);

    const { response, error } = await handleResponse<IServicepointLimitsData, IResponseError>(
      Vue.$axios.get(`${BACKEND_BASE_URL}/api/configuration/v1/service-point/limits`, { params })
    );
    if (error) {
      return undefined;
    } else {
      return response?.data;
    }
  }

  async saveUpdateServicepoint(
    updateServicepoint: IUpdateServicepoint
  ): Promise<IGenericResponse<IServicepointDetail> | undefined> {
    const { response, error } = await handleResponse<
      IGenericResponse<IServicepointDetail>,
      IResponseError
    >(
      Vue.$axios.post(`${BACKEND_BASE_URL}/api/configuration/v1/service-point`, updateServicepoint)
    );
    if (error) {
      if (error.response?.status === 400 && error.response?.data?.message) {
        Vue.swal({
          title: i18n.t("option.type.error"),
          text: i18n.t(error.response.data.message),
          icon: "warning",
        });
      }
    } else {
      return response?.data;
    }
  }

  async updateStatusServicepoint(idServicepoint: number): Promise<number | undefined> {
    const { response, error } = await handleResponse<number, IResponseError>(
      Vue.$axios.patch(`${BACKEND_BASE_URL}/api/configuration/v1/service-point/` + idServicepoint)
    );
    if (error) {
      return undefined;
    } else {
      return response?.data;
    }
  }

  async postGenerateServicePointReport(request?: IServicePointReportRequest): Promise<void> {
    await handleResponse<Promise<void>, IResponseError>(
      Vue.$axios.post(`${BACKEND_BASE_URL}/api/configuration/v1/report/service-point`, request)
    );
  }

  async syncServicepoint(id: number): Promise<ServicepointServiceSyncResponse | undefined> {
    const { response, error } = await handleResponse<IServicepointDetailData, IResponseError>(
      Vue.$axiosSilentLoading.post(
        `${BACKEND_BASE_URL}/api/configuration/v1/service-point/${id}/sync`
      )
    );
    if (error) {
      const errorData = error.response;
      if (errorData?.status != 500 && errorData?.data) {
        return new ServicepointServiceSyncResponse(false, null, errorData?.data?.message);
      }
      return undefined;
    } else {
      return new ServicepointServiceSyncResponse(true, response!.data);
    }
  }
}
